const BOOLEAN_TOKEN_PREFIX_PATTERN = /^(show|is)/

/**
 * Canonical catalogs of editable branding tokens.
 *
 * Authored once here so both the organization-level branded content inspector and the
 * per-surface curated manifest reference the same paths. Paths map to
 * BrandingConfig in `@feed-widget/branding`; CSS vars are produced by
 * brandingToCssVars().
 */

export const SIZE_UNIT_OPTIONS = ['px', '%', 'rem', 'em', 'vh', 'vw']

export const TEXT_TRANSFORM_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'uppercase', label: 'Uppercase' },
  { value: 'capitalize', label: 'Capitalize' },
  { value: 'lowercase', label: 'Lowercase' },
]

export const BASIC_COLOR_FIELDS = [
  { path: 'colors.background', label: 'Background' },
  { path: 'colors.primary', label: 'Primary' },
  { path: 'colors.secondary', label: 'Secondary' },
  { path: 'colors.highlight', label: 'Highlight' },
  { path: 'colors.heading', label: 'Heading' },
  { path: 'colors.subheading', label: 'Subheading' },
  { path: 'colors.buttonTextPrimary', label: 'Button Text' },
  { path: 'colors.borderColor', label: 'Border' },
]

export const STATE_COLOR_FIELDS = [
  { path: 'colors.success', label: 'Success' },
  { path: 'colors.warning', label: 'Warning' },
  { path: 'colors.error', label: 'Error' },
  { path: 'colors.buttonTextSecondary', label: 'Secondary Button Text' },
]

export const GRADIENT_FIELDS = [
  { path: 'gradients.primary', label: 'Primary gradient' },
  { path: 'gradients.secondary', label: 'Secondary gradient' },
  { path: 'gradients.shared', label: 'Shared gradient' },
]

export const TYPOGRAPHY_GROUPS = [
  { label: 'Username', path: 'typography.username' },
  { label: 'Timestamp', path: 'typography.timestamp' },
  { label: 'Body', path: 'typography.body' },
  { label: 'Bet label', path: 'typography.betLabel' },
  { label: 'Odds', path: 'typography.odds' },
  { label: 'Leg event', path: 'typography.legEvent' },
  { label: 'Leg market', path: 'typography.legMarket' },
  { label: 'Leg selection', path: 'typography.legSelection' },
  { label: 'CTA', path: 'typography.cta' },
  { label: 'Copy count', path: 'typography.copyCount' },
  { label: 'Tag', path: 'typography.tag' },
  { label: 'Badge', path: 'typography.badge' },
  { label: 'Compact avatar name', path: 'typography.compactAvatarName' },
]

export const TYPOGRAPHY_SUBFIELDS = [
  { key: 'fontSize', label: 'Size', kind: 'size' },
  { key: 'fontSizeDesktop', label: 'Desktop size', kind: 'size' },
  { key: 'fontWeight', label: 'Weight', kind: 'text' },
  { key: 'fontFamily', label: 'Font family', kind: 'text' },
  {
    key: 'fontStyle',
    label: 'Style',
    kind: 'select',
    options: [
      { value: 'normal', label: 'Normal' },
      { value: 'italic', label: 'Italic' },
    ],
  },
  { key: 'lineHeight', label: 'Line height', kind: 'text' },
  { key: 'lineHeightDesktop', label: 'Desktop line height', kind: 'text' },
  { key: 'letterSpacing', label: 'Letter spacing', kind: 'size' },
  { key: 'textTransform', label: 'Transform', kind: 'select', options: TEXT_TRANSFORM_OPTIONS },
]

export const LAYOUT_GROUPS = [
  {
    title: 'Feed shell',
    fields: [
      { path: 'layout.feed.maxWidth', label: 'Max width' },
      { path: 'layout.feed.maxWidthDesktop', label: 'Desktop max width' },
      { path: 'layout.feed.paddingX', label: 'Horizontal padding' },
      { path: 'layout.feed.paddingY', label: 'Vertical padding' },
      { path: 'layout.feed.cardGap', label: 'Card gap' },
      { path: 'layout.feed.bannerGap', label: 'Banner gap' },
      { path: 'layout.feed.endLineWidth', label: 'End line width' },
    ],
  },
  {
    title: 'Card',
    fields: [
      { path: 'layout.card.borderWidth', label: 'Border width' },
      { path: 'layout.card.borderRadius', label: 'Border radius' },
      { path: 'layout.card.headerPaddingX', label: 'Header padding X' },
      { path: 'layout.card.headerPaddingY', label: 'Header padding Y' },
      { path: 'layout.card.contentPaddingX', label: 'Content padding X' },
      { path: 'layout.card.contentPaddingY', label: 'Content padding Y' },
      { path: 'layout.card.contentPaddingTop', label: 'Content padding top' },
      { path: 'layout.card.contentPaddingBottom', label: 'Content padding bottom' },
    ],
  },
  {
    title: 'Header',
    fields: [
      { path: 'layout.header.gap', label: 'Header gap' },
      { path: 'layout.header.avatarSize', label: 'Avatar size' },
    ],
  },
  {
    title: 'Media',
    fields: [
      { path: 'layout.media.gridGap', label: 'Grid gap' },
      { path: 'layout.media.aspectRatio', label: 'Aspect ratio' },
      { path: 'layout.media.objectFit', label: 'Object fit' },
      { path: 'layout.media.objectPosition', label: 'Object position' },
      { path: 'layout.media.playOverlaySize', label: 'Play overlay size' },
      { path: 'layout.media.controlButtonSize', label: 'Control button size' },
      { path: 'layout.media.controlGap', label: 'Control gap' },
      { path: 'layout.media.controlInset', label: 'Control inset' },
      { path: 'layout.media.videoMaxHeight', label: 'Video max height' },
    ],
  },
  {
    title: 'Betslip',
    fields: [
      { path: 'layout.betslip.outerPaddingX', label: 'Outer padding X' },
      { path: 'layout.betslip.outerPaddingY', label: 'Outer padding Y' },
      { path: 'layout.betslip.panelPaddingX', label: 'Panel padding X' },
      { path: 'layout.betslip.panelPaddingY', label: 'Panel padding Y' },
      { path: 'layout.betslip.panelGap', label: 'Panel section gap' },
      { path: 'layout.betslip.panelBorderWidth', label: 'Panel border width' },
      { path: 'layout.betslip.panelBorderRadius', label: 'Panel radius' },
      { path: 'layout.betslip.panelMinHeight', label: 'Panel minimum height' },
      { path: 'layout.betslip.rowGap', label: 'Leg row gap' },
      { path: 'layout.betslip.topLineGap', label: 'Topline row gap' },
      { path: 'layout.betslip.metaGap', label: 'Topline item gap' },
      { path: 'layout.betslip.topLinePaddingX', label: 'Topline padding X' },
      { path: 'layout.betslip.topLinePaddingY', label: 'Topline padding Y' },
      { path: 'layout.betslip.legPaddingX', label: 'Leg padding X' },
      { path: 'layout.betslip.legPaddingY', label: 'Leg padding Y' },
      { path: 'layout.betslip.legGap', label: 'Leg gap' },
      { path: 'layout.betslip.connectorWidth', label: 'Connector width' },
      { path: 'layout.betslip.connectorDotSize', label: 'Connector dot size' },
      { path: 'layout.betslip.connectorDotBorderWidth', label: 'Connector dot border' },
      { path: 'layout.betslip.connectorCoreSize', label: 'Connector core size' },
      { path: 'layout.betslip.connectorHaloWidth', label: 'Connector halo width' },
    ],
  },
  {
    title: 'Same game parlay',
    fields: [
      { path: 'layout.betslip.sgpLabelPaddingX', label: 'Badge padding X' },
      { path: 'layout.betslip.sgpLabelPaddingY', label: 'Badge padding Y' },
      { path: 'layout.betslip.sgpLabelPaddingTop', label: 'Badge padding top' },
      { path: 'layout.betslip.sgpLabelPaddingBottom', label: 'Badge padding bottom' },
      { path: 'layout.betslip.sgpLabelRadius', label: 'Badge corner radius' },
      { path: 'layout.betslip.sgpAccentWidth', label: 'Group accent width' },
    ],
  },
  {
    title: 'CTA',
    fields: [
      { path: 'layout.cta.actionsPaddingX', label: 'Actions padding X' },
      { path: 'layout.cta.actionsPaddingY', label: 'Actions padding Y' },
      { path: 'layout.cta.buttonHeight', label: 'Button height' },
      { path: 'layout.cta.copyCountPaddingTop', label: 'Copy count padding top' },
      { path: 'layout.cta.copyCountPaddingBottom', label: 'Copy count padding bottom' },
      { path: 'layout.cta.paddingX', label: 'CTA padding X' },
      { path: 'layout.cta.paddingY', label: 'CTA padding Y' },
      { path: 'layout.cta.borderRadius', label: 'CTA radius' },
      { path: 'layout.cta.buttonTopRadius', label: 'CTA top radius' },
      { path: 'layout.cta.warningPaddingX', label: 'Warning padding X' },
      { path: 'layout.cta.warningPaddingY', label: 'Warning padding Y' },
    ],
  },
  {
    title: 'Tags',
    fields: [
      { path: 'layout.tags.inlineGap', label: 'Inline gap' },
      { path: 'layout.tags.inlinePaddingX', label: 'Inline padding X' },
      { path: 'layout.tags.inlinePaddingY', label: 'Inline padding Y' },
      { path: 'layout.tags.headerGap', label: 'Header gap' },
      { path: 'layout.tags.headerMarginLeft', label: 'Header offset' },
      { path: 'layout.tags.chipGap', label: 'Chip gap' },
      { path: 'layout.tags.chipPaddingX', label: 'Chip padding X' },
      { path: 'layout.tags.chipPaddingY', label: 'Chip padding Y' },
      { path: 'layout.tags.chipRadius', label: 'Chip radius' },
    ],
  },
  {
    title: 'Compact card',
    fields: [
      { path: 'layout.compactCard.widthMobile', label: 'Mobile width' },
      { path: 'layout.compactCard.heightMobile', label: 'Mobile height' },
      { path: 'layout.compactCard.widthDesktop', label: 'Desktop width' },
      { path: 'layout.compactCard.heightDesktop', label: 'Desktop height' },
      { path: 'layout.compactCard.borderWidth', label: 'Card border width' },
      { path: 'layout.compactCard.borderRadius', label: 'Card radius' },
      { path: 'layout.compactCard.avatarTop', label: 'Avatar offset top' },
      { path: 'layout.compactCard.avatarLeft', label: 'Avatar offset left' },
      { path: 'layout.compactCard.avatarGap', label: 'Avatar gap' },
      { path: 'layout.compactCard.avatarSize', label: 'Avatar size' },
      { path: 'layout.compactCard.legsPaddingX', label: 'Legs padding X' },
      { path: 'layout.compactCard.legsPaddingY', label: 'Legs padding Y' },
      { path: 'layout.compactCard.actionPaddingX', label: 'Action padding X' },
      { path: 'layout.compactCard.actionPaddingY', label: 'Action padding Y' },
      { path: 'layout.compactCard.ctaRadius', label: 'CTA radius' },
      { path: 'layout.compactCard.drawerRadius', label: 'Drawer radius' },
      { path: 'layout.compactCard.drawerMaxHeight', label: 'Drawer max height' },
      { path: 'layout.compactCard.drawerPaddingX', label: 'Drawer padding X' },
      { path: 'layout.compactCard.drawerPaddingY', label: 'Drawer padding Y' },
    ],
  },
  {
    title: 'Banner',
    fields: [
      { path: 'layout.banner.borderRadius', label: 'Banner radius' },
      { path: 'layout.banner.spacingBottom', label: 'Banner spacing bottom' },
    ],
  },
]

export const EFFECT_FIELDS = [
  { path: 'effects.cardShadow', label: 'Card shadow', kind: 'text' },
  { path: 'effects.cardHoverShadow', label: 'Card hover shadow', kind: 'text' },
  { path: 'effects.cardHoverLift', label: 'Card hover lift', kind: 'size' },
  { path: 'effects.ctaShadow', label: 'CTA shadow', kind: 'text' },
  { path: 'effects.avatarShadow', label: 'Avatar shadow', kind: 'text' },
  { path: 'effects.mediaControlBackdrop', label: 'Media control backdrop', kind: 'text' },
  { path: 'effects.mediaControlHoverBackdrop', label: 'Media control hover backdrop', kind: 'text' },
  { path: 'effects.playOverlayBackdrop', label: 'Play overlay backdrop', kind: 'text' },
  { path: 'effects.compactDrawerShadow', label: 'Compact drawer shadow', kind: 'text' },
  { path: 'effects.compactBackdrop', label: 'Compact backdrop', kind: 'text' },
  { path: 'effects.overlayTextShadow', label: 'Overlay text shadow', kind: 'text' },
]

// Winning-ticket card look and copy. Also edited from the Configuration page's
// generated-posts section (GeneratedPostAppearanceForm).
export const WINNING_TICKET_GROUP = Object.freeze({
  title: 'Winning ticket',
  fields: [
    { path: 'components.winningTicket.headerText', label: 'Header text', kind: 'text' },
    { path: 'components.winningTicket.headerIconUrl', label: 'Header icon URL', kind: 'text' },
    { path: 'components.winningTicket.categoryIconUrl', label: 'Category icon URL', kind: 'text' },
    { path: 'components.winningTicket.feedCreditName', label: 'Feed credit name', kind: 'text' },
    { path: 'components.winningTicket.headerBackground', label: 'Header background', kind: 'color' },
    { path: 'components.winningTicket.headerIconColor', label: 'Header icon color', kind: 'color' },
    { path: 'components.winningTicket.bodyBackground', label: 'Ticket background', kind: 'color' },
    { path: 'components.winningTicket.textColor', label: 'Text', kind: 'color' },
    { path: 'components.winningTicket.successColor', label: 'Success', kind: 'color' },
    { path: 'components.winningTicket.oddsColor', label: 'Odds', kind: 'color' },
    { path: 'components.winningTicket.dividerColor', label: 'Divider', kind: 'color' },
    { path: 'components.winningTicket.ctaBackground', label: 'Follow CTA background', kind: 'color' },
    { path: 'components.winningTicket.ctaColor', label: 'Follow CTA text', kind: 'color' },
    { path: 'components.winningTicket.ctaBorderRadius', label: 'Follow CTA radius', kind: 'size' },
    { path: 'components.winningTicket.ctaFontWeight', label: 'Follow CTA weight', kind: 'text' },
  ],
})

// The look of a generated post (widget `GeneratedCardTokens`) on the standard
// card of its content origin. Every token empty inherits the card's normal
// look; an empty badge text shows no badge. An empty persona name or avatar
// URL keeps the persona creator's own (seeded as "Personalized Bets").
export const PERSONA_CREATOR_DEFAULT_NAME = 'Personalized Bets'

function generatedPostTokenGroup(title, group) {
  const prefix = `components.${group}.`
  return Object.freeze({
    title,
    fields: [
      { path: `${prefix}personaName`, label: 'Persona name', kind: 'text', placeholder: PERSONA_CREATOR_DEFAULT_NAME },
      { path: `${prefix}personaAvatarUrl`, label: 'Persona avatar URL', kind: 'text', placeholder: "Defaults to the persona creator's avatar" },
      { path: `${prefix}badgeText`, label: 'Badge text', kind: 'text' },
      { path: `${prefix}badgeBackground`, label: 'Badge background', kind: 'color' },
      { path: `${prefix}badgeColor`, label: 'Badge text color', kind: 'color' },
      { path: `${prefix}cardBackground`, label: 'Card background', kind: 'color' },
      { path: `${prefix}borderColor`, label: 'Border / accent', kind: 'color' },
      { path: `${prefix}textColor`, label: 'Text', kind: 'color' },
      { path: `${prefix}oddsColor`, label: 'Odds', kind: 'color' },
      { path: `${prefix}ctaBackground`, label: 'CTA background', kind: 'color' },
      { path: `${prefix}ctaColor`, label: 'CTA text', kind: 'color' },
      { path: `${prefix}ctaBorderRadius`, label: 'CTA radius', kind: 'size' },
    ],
  })
}

// AI generated posts (content origin personalized_recommendation).
export const CROWD_RECOMMENDATION_GROUP = generatedPostTokenGroup('AI generated post', 'crowdRecommendation')

// Users generated posts (content origin personalized_ticket).
export const PERSONALIZED_TICKET_GROUP = generatedPostTokenGroup('Users generated post', 'personalizedTicket')

export const COMPONENT_GROUPS = [
  {
    title: 'Follow button',
    fields: [
      { path: 'components.followButton.paddingX', label: 'Padding X', kind: 'size' },
      { path: 'components.followButton.paddingY', label: 'Padding Y', kind: 'size' },
      { path: 'components.followButton.borderRadius', label: 'Radius', kind: 'size' },
      { path: 'components.followButton.fontSize', label: 'Font size', kind: 'size' },
      { path: 'components.followButton.fontWeight', label: 'Font weight', kind: 'text' },
      { path: 'components.followButton.followingBackgroundDark', label: 'Following bg (dark)', kind: 'color' },
      { path: 'components.followButton.followingBackgroundLight', label: 'Following bg (light)', kind: 'color' },
    ],
  },
  {
    title: 'Promoted badge',
    fields: [
      { path: 'components.promotedBadge.paddingX', label: 'Padding X', kind: 'size' },
      { path: 'components.promotedBadge.paddingY', label: 'Padding Y', kind: 'size' },
      { path: 'components.promotedBadge.borderRadius', label: 'Radius', kind: 'size' },
      { path: 'components.promotedBadge.fontSize', label: 'Font size', kind: 'size' },
      { path: 'components.promotedBadge.fontWeight', label: 'Font weight', kind: 'text' },
      { path: 'components.promotedBadge.letterSpacing', label: 'Letter spacing', kind: 'size' },
      { path: 'components.promotedBadge.backgroundDark', label: 'Background dark', kind: 'color' },
      { path: 'components.promotedBadge.backgroundLight', label: 'Background light', kind: 'color' },
      { path: 'components.promotedBadge.colorDark', label: 'Text dark', kind: 'color' },
      { path: 'components.promotedBadge.colorLight', label: 'Text light', kind: 'color' },
    ],
  },
  {
    title: 'Betslip',
    fields: [
      { path: 'components.betslip.panelBackground', label: 'Panel background', kind: 'color' },
      { path: 'components.betslip.panelBorderColor', label: 'Panel border', kind: 'color' },
      { path: 'components.betslip.rowEvenDark', label: 'Even row (dark)', kind: 'color' },
      { path: 'components.betslip.rowOddDark', label: 'Odd row (dark)', kind: 'color' },
      { path: 'components.betslip.rowEvenLight', label: 'Even row (light)', kind: 'color' },
      { path: 'components.betslip.rowOddLight', label: 'Odd row (light)', kind: 'color' },
      { path: 'components.betslip.connectorLineColor', label: 'Connector rail color (CSS)', kind: 'text' },
      { path: 'components.betslip.connectorMarkerColor', label: 'Connector marker color (auto or CSS)', kind: 'text' },
      { path: 'components.betslip.connectorMarkerFill', label: 'Connector marker fill (CSS)', kind: 'text' },
      { path: 'components.betslip.liveBadgeBackground', label: 'Live badge bg', kind: 'color' },
      { path: 'components.betslip.liveBadgeColor', label: 'Live badge text', kind: 'color' },
      { path: 'components.betslip.warningBackground', label: 'Warning bg', kind: 'color' },
    ],
  },
  {
    title: 'Same game parlay badge',
    fields: [
      { path: 'components.betslip.sgpLabelBackground', label: 'Badge background', kind: 'color' },
      { path: 'components.betslip.sgpLabelColor', label: 'Badge text', kind: 'color' },
      { path: 'components.betslip.sgpLabelFontFamily', label: 'Font family', kind: 'text' },
      { path: 'components.betslip.sgpLabelFontSize', label: 'Font size', kind: 'size' },
      { path: 'components.betslip.sgpLabelFontWeight', label: 'Font weight', kind: 'text' },
      { path: 'components.betslip.sgpLabelFontStyle', label: 'Font style', kind: 'text' },
      { path: 'components.betslip.sgpLabelLineHeight', label: 'Line height', kind: 'text' },
      { path: 'components.betslip.sgpLabelLetterSpacing', label: 'Letter spacing', kind: 'size' },
      {
        path: 'components.betslip.sgpLabelTextTransform',
        label: 'Text transform',
        kind: 'select',
        options: TEXT_TRANSFORM_OPTIONS,
      },
      { path: 'components.betslip.sgpLabelSkew', label: 'Background skew', kind: 'text' },
    ],
  },
  {
    title: 'Status badges',
    fields: [
      { path: 'components.statusBadges.wonBackground', label: 'Won bg', kind: 'color' },
      { path: 'components.statusBadges.wonColor', label: 'Won text', kind: 'color' },
      { path: 'components.statusBadges.lostBackground', label: 'Lost bg', kind: 'color' },
      { path: 'components.statusBadges.lostColor', label: 'Lost text', kind: 'color' },
      { path: 'components.statusBadges.suspendedBackground', label: 'Suspended bg', kind: 'color' },
      { path: 'components.statusBadges.suspendedColor', label: 'Suspended text', kind: 'color' },
      { path: 'components.statusBadges.closedBackgroundDark', label: 'Closed bg (dark)', kind: 'color' },
      { path: 'components.statusBadges.closedBackgroundLight', label: 'Closed bg (light)', kind: 'color' },
      { path: 'components.statusBadges.closedColor', label: 'Closed text', kind: 'color' },
    ],
  },
  {
    title: 'CTA states',
    fields: [
      { path: 'components.cta.background', label: 'Background (CSS)', kind: 'text' },
      { path: 'components.cta.color', label: 'Text', kind: 'color' },
      { path: 'components.cta.borderWidth', label: 'Border width', kind: 'size' },
      { path: 'components.cta.borderBottomWidth', label: 'Bottom border width', kind: 'size' },
      { path: 'components.cta.borderColor', label: 'Border color (CSS)', kind: 'text' },
      { path: 'components.cta.hoverBackground', label: 'Hover background (CSS)', kind: 'text' },
      { path: 'components.cta.addedBackground', label: 'Added bg', kind: 'color' },
      { path: 'components.cta.addedColor', label: 'Added text', kind: 'color' },
      { path: 'components.cta.disabledOpacity', label: 'Disabled opacity', kind: 'text' },
    ],
  },
  {
    title: 'Prepack footer',
    fields: [
      { path: 'components.prepackActions.background', label: 'Footer background (CSS)', kind: 'text' },
      { path: 'components.prepackActions.borderColor', label: 'Footer divider color', kind: 'color' },
    ],
  },
  {
    title: 'Prepack odds',
    fields: [
      { path: 'components.prepackOdds.background', label: 'Odds background (CSS)', kind: 'text' },
      { path: 'components.prepackOdds.color', label: 'Odds text', kind: 'color' },
      { path: 'components.prepackOdds.borderColor', label: 'Odds border color', kind: 'color' },
      { path: 'components.prepackOdds.borderWidth', label: 'Odds border width', kind: 'size' },
      { path: 'components.prepackOdds.borderRadius', label: 'Odds radius', kind: 'size' },
      { path: 'components.prepackOdds.paddingX', label: 'Odds padding X', kind: 'size' },
      { path: 'components.prepackOdds.paddingY', label: 'Odds padding Y', kind: 'size' },
    ],
  },
  {
    title: 'Share button',
    fields: [
      { path: 'components.shareButton.background', label: 'Background (CSS)', kind: 'text' },
      { path: 'components.shareButton.color', label: 'Text', kind: 'color' },
      { path: 'components.shareButton.borderColor', label: 'Border color (CSS)', kind: 'text' },
      { path: 'components.shareButton.borderWidth', label: 'Border width', kind: 'size' },
      { path: 'components.shareButton.borderRadius', label: 'Radius', kind: 'size' },
      { path: 'components.shareButton.paddingX', label: 'Padding X', kind: 'size' },
      { path: 'components.shareButton.paddingY', label: 'Padding Y', kind: 'size' },
      { path: 'components.shareButton.gap', label: 'Icon gap', kind: 'size' },
      { path: 'components.shareButton.minHeight', label: 'Minimum height', kind: 'size' },
      { path: 'components.shareButton.fontSize', label: 'Font size', kind: 'size' },
      { path: 'components.shareButton.fontWeight', label: 'Font weight', kind: 'text' },
      { path: 'components.shareButton.lineHeight', label: 'Line height', kind: 'text' },
      { path: 'components.shareButton.iconSize', label: 'Icon size', kind: 'size' },
    ],
  },
  {
    title: 'Copy count',
    fields: [
      { path: 'components.copyCount.backgroundDark', label: 'Background (dark)', kind: 'color' },
      { path: 'components.copyCount.backgroundLight', label: 'Background (light)', kind: 'color' },
      { path: 'components.copyCount.colorDark', label: 'Count (dark)', kind: 'color' },
      { path: 'components.copyCount.colorLight', label: 'Count (light)', kind: 'color' },
      { path: 'components.copyCount.labelColorDark', label: 'Label (dark)', kind: 'color' },
      { path: 'components.copyCount.labelColorLight', label: 'Label (light)', kind: 'color' },
      { path: 'components.copyCount.iconColorDark', label: 'Icon (dark)', kind: 'color' },
      { path: 'components.copyCount.iconColorLight', label: 'Icon (light)', kind: 'color' },
      { path: 'components.copyCount.borderColorDark', label: 'Divider (dark)', kind: 'color' },
      { path: 'components.copyCount.borderColorLight', label: 'Divider (light)', kind: 'color' },
    ],
  },
  {
    title: 'Compact card',
    fields: [
      { path: 'components.compactCard.background', label: 'Background', kind: 'gradient' },
      { path: 'components.compactCard.borderColor', label: 'Border', kind: 'color' },
      { path: 'components.compactCard.boxShadow', label: 'Shadow', kind: 'text' },
      { path: 'components.compactCard.overlayTextColor', label: 'Overlay text', kind: 'color' },
      { path: 'components.compactCard.avatarBorderColor', label: 'Avatar border', kind: 'color' },
      { path: 'components.compactCard.avatarBorderWidth', label: 'Avatar border width', kind: 'size' },
      { path: 'components.compactCard.expandBackground', label: 'Expand bg', kind: 'color' },
      { path: 'components.compactCard.expandHoverBackground', label: 'Expand hover bg', kind: 'color' },
      { path: 'components.compactCard.drawerCloseBackground', label: 'Drawer close bg', kind: 'color' },
      { path: 'components.compactCard.drawerCloseHoverBackground', label: 'Drawer close hover bg', kind: 'color' },
      { path: 'components.compactCard.drawerEvenRowBackground', label: 'Drawer row bg', kind: 'color' },
    ],
  },
  {
    title: 'Feed states',
    fields: [
      { path: 'components.feedStates.loadingTextColor', label: 'Loading text', kind: 'color' },
      { path: 'components.feedStates.errorTitleColor', label: 'Error title', kind: 'color' },
      { path: 'components.feedStates.emptyHeadingColor', label: 'Empty heading', kind: 'color' },
      { path: 'components.feedStates.emptySubheadingColor', label: 'Empty subheading', kind: 'color' },
      { path: 'components.feedStates.retryButtonRadius', label: 'Retry button radius', kind: 'size' },
    ],
  },
  WINNING_TICKET_GROUP,
  CROWD_RECOMMENDATION_GROUP,
  PERSONALIZED_TICKET_GROUP,
]

export const PRESENTATION_GROUPS = [
  {
    title: 'Card behavior',
    fields: [
      { path: 'presentation.showFollowButton', label: 'Show follow button', kind: 'toggle' },
      {
        path: 'presentation.shareLabelMode',
        label: 'Share label',
        kind: 'select',
        options: [
          { value: 'responsive', label: 'Hide on narrow screens' },
          { value: 'always', label: 'Always show' },
          { value: 'hidden', label: 'Always hide' },
        ],
      },
    ],
  },
  {
    title: 'Betslip presentation',
    fields: [
      {
        path: 'presentation.betslip.variant',
        label: 'Layout',
        kind: 'select',
        options: [
          { value: 'rows', label: 'Rows' },
          { value: 'panel', label: 'Panel' },
        ],
      },
      { path: 'presentation.betslip.labelTemplate', label: 'Same game parlay label', kind: 'text' },
      { path: 'presentation.betslip.parlayLabelTemplate', label: 'Parlay label', kind: 'text' },
      { path: 'presentation.betslip.showSingleLabel', label: 'Show single label', kind: 'toggle' },
      { path: 'presentation.betslip.showToplineEvent', label: 'Show topline event', kind: 'toggle' },
      { path: 'presentation.betslip.showGroupLabel', label: 'Show group label', kind: 'toggle' },
      { path: 'presentation.betslip.showLegEvent', label: 'Show event on each leg', kind: 'toggle' },
      { path: 'presentation.betslip.connectMultipleSingles', label: 'Connect multiple singles', kind: 'toggle' },
      {
        path: 'presentation.betslip.badgePosition',
        label: 'SGP badge position',
        kind: 'select',
        options: [
          { value: 'after-label', label: 'After label' },
          { value: 'before-label', label: 'Before label' },
        ],
      },
      {
        path: 'presentation.betslip.oddsPosition',
        label: 'Combined odds position',
        kind: 'select',
        options: [
          { value: 'trailing', label: 'Trailing' },
          { value: 'inline', label: 'Inline' },
        ],
      },
    ],
  },
]

export const ASSET_FIELDS = [
  { path: 'assets.mediaPlayIconUrl', label: 'Play icon URL', kind: 'text' },
  { path: 'assets.mediaPauseIconUrl', label: 'Pause icon URL', kind: 'text' },
  { path: 'assets.mediaMutedIconUrl', label: 'Muted icon URL', kind: 'text' },
  { path: 'assets.shareIconUrl', label: 'Share icon URL', kind: 'text' },
  { path: 'assets.copyCountIconUrl', label: 'Copy count icon URL', kind: 'text' },
  { path: 'assets.betslipMarkerIconUrl', label: 'Betslip marker icon URL', kind: 'text' },
]

/**
 * Flattens a path to its token kind so TokenField can render the right
 * control when the caller only passes a path. Used for validation and for
 * "surface overrides a token that isn't in this surface's manifest" hints.
 */
export function inferKindFromPath(path) {
  if (!path) return 'text'
  if (path.startsWith('colors.')) return 'color'
  if (path.startsWith('gradients.')) return 'gradient'
  if (path.startsWith('typography.')) return 'text'
  if (path.startsWith('layout.')) return 'size'
  if (path.startsWith('effects.')) return 'text'
  if (path === 'buttonText') return 'text'
  if (path === 'darkMode') return 'toggle'
  if (path.startsWith('presentation.') && BOOLEAN_TOKEN_PREFIX_PATTERN.test(path.split('.').pop())) return 'toggle'
  return 'text'
}

/**
 * Every dot-path the baseline (org branding) inspector renders as a
 * TokenField. This is the "active path" set for click-to-edit resolution
 * in baseline mode — the preview only focuses fields that actually exist
 * in the inspector. (darkMode is a Switch, not a TokenField, so it is
 * intentionally absent.)
 */
export function getBaselineTokenPaths() {
  return [
    'buttonText',
    'copyCountLabel',
    'logoUrl',
    'fonts.primary',
    'fontUrls',
    ...BASIC_COLOR_FIELDS.map((field) => field.path),
    ...STATE_COLOR_FIELDS.map((field) => field.path),
    ...GRADIENT_FIELDS.map((field) => field.path),
    ...TYPOGRAPHY_GROUPS.flatMap((group) =>
      TYPOGRAPHY_SUBFIELDS.map((sub) => `${group.path}.${sub.key}`)
    ),
    ...LAYOUT_GROUPS.flatMap((group) => group.fields.map((field) => field.path)),
    ...EFFECT_FIELDS.map((field) => field.path),
    ...COMPONENT_GROUPS.flatMap((group) => group.fields.map((field) => field.path)),
    ...PRESENTATION_GROUPS.flatMap((group) => group.fields.map((field) => field.path)),
    ...ASSET_FIELDS.map((field) => field.path),
    'banner.imageUrl',
    'banner.linkUrl',
    'banner.altText',
  ]
}
